import configData from "../config.json";
import React from "react";
import { setUserSession } from "../components/Session";
import biglogo from "../assets/biglogo.png";
import loginBackground from "../assets/login-weighbridge-background.png";

const TURNSTILE_SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const TURNSTILE_LOCAL_TEST_SITE_KEY = "1x00000000000000000000AA";

const getTurnstileSiteKey = () => {
    if (typeof window === "undefined") return configData.TURNSTILE_SITE_KEY || "";

    const localHostnames = ["localhost", "127.0.0.1", "0.0.0.0"];
    if (localHostnames.includes(window.location.hostname)) {
        return configData.TURNSTILE_LOCAL_SITE_KEY || TURNSTILE_LOCAL_TEST_SITE_KEY;
    }

    return configData.TURNSTILE_SITE_KEY || "";
};

const SignIn = () => {
    let u = localStorage.getItem("WBU") || "";
    let p = localStorage.getItem("WBP") || "";
    const [username, setUsername] = React.useState(u);
    const [password, setPassword] = React.useState(p);
    const [fail, setFail] = React.useState(false);
    const [loginSuccess, setLoginSuccess] = React.useState(false);
    const [showPassword, setShowPassword] = React.useState(false);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [turnstileToken, setTurnstileToken] = React.useState("");
    const [turnstileError, setTurnstileError] = React.useState("");
    const turnstileRef = React.useRef(null);
    const turnstileWidgetId = React.useRef(null);
    const turnstileSiteKey = React.useMemo(() => getTurnstileSiteKey(), []);

    React.useEffect(() => {
        if (!turnstileSiteKey || !turnstileRef.current) return;

        let cancelled = false;

        const renderTurnstile = () => {
            if (cancelled || !window.turnstile || !turnstileRef.current || turnstileWidgetId.current !== null) return;

            turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
                sitekey: turnstileSiteKey,
                theme: "light",
                action: "login",
                callback: (token) => {
                    setTurnstileToken(token);
                    setTurnstileError("");
                },
                "expired-callback": () => {
                    setTurnstileToken("");
                    setTurnstileError("Security check expired. Please verify again.");
                },
                "error-callback": () => {
                    setTurnstileToken("");
                    setTurnstileError("Security check failed to load. Please try again.");
                },
            });
        };

        const existingScript = document.querySelector(`script[src="${TURNSTILE_SCRIPT_SRC}"]`);
        if (existingScript) {
            if (window.turnstile) renderTurnstile();
            else existingScript.addEventListener("load", renderTurnstile, { once: true });
        } else {
            const script = document.createElement("script");
            script.src = TURNSTILE_SCRIPT_SRC;
            script.async = true;
            script.defer = true;
            script.onload = renderTurnstile;
            script.onerror = () => setTurnstileError("Security check failed to load. Please refresh the page.");
            document.body.appendChild(script);
        }

        return () => {
            cancelled = true;
            if (window.turnstile && turnstileWidgetId.current !== null) {
                window.turnstile.remove(turnstileWidgetId.current);
                turnstileWidgetId.current = null;
            }
        };
    }, [turnstileSiteKey]);

    React.useEffect(() => {
        if (u !== "" && p !== "" && (!turnstileSiteKey || turnstileToken)) submitted();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [turnstileToken]);

    const resetTurnstile = () => {
        setTurnstileToken("");
        if (window.turnstile && turnstileWidgetId.current !== null) {
            window.turnstile.reset(turnstileWidgetId.current);
        }
    };

    const submitted = async () => {
        if (isSubmitting) return;

        if (turnstileSiteKey && !turnstileToken) {
            setTurnstileError("Please complete the security check before signing in.");
            return;
        }

        setIsSubmitting(true);
        setFail(false);
        setTurnstileError("");

        try {
            const response = await fetch(`${configData.SERVER_URL}apiV7/Auth`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                mode: "cors",
                body: JSON.stringify({ username, password }),
            });

            if (response.ok) {
                const data = await response.json();
                setLoginSuccess(true);
                setUserSession(data);

                if (data.autologin) {
                    localStorage.setItem("WBU", username);
                    localStorage.setItem("WBP", password);
                } else {
                    localStorage.setItem("WBU", "");
                    localStorage.setItem("WBP", "");
                }

                setTimeout(() => {
                    window.location.href = data.initialPage;
                }, 900);
            } else {
                setFail(true);
                resetTurnstile();
                localStorage.setItem("WBU", "");
                localStorage.setItem("WBP", "");
            }
        } catch (error) {
            setFail(true);
            resetTurnstile();
            console.error("Login error:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const submitOnEnter = (event) => {
        if (event.key === "Enter") submitted();
    };

    return (
        <>
            <style>{`
                html, body, #root {
                    min-height: 100%;
                    margin: 0;
                }

                body {
                    overflow: hidden;
                    font-family: "Segoe UI", Arial, sans-serif;
                }

                .wm-login-page {
                    position: relative;
                    min-height: 100vh;
                    display: grid;
                    place-items: center;
                    overflow: hidden;
                    color: #102557;
                    background:
                        radial-gradient(circle at 50% 34%, rgba(255, 255, 255, 0.38), rgba(255, 255, 255, 0.12) 34%, transparent 62%),
                        linear-gradient(145deg, #eef7ff 0%, #dbeeff 46%, #f8fbff 100%);
                }

                .wm-login-page::before {
                    content: "";
                    position: absolute;
                    inset: 0;
                    z-index: 0;
                    background:
                        linear-gradient(180deg, rgba(245, 250, 255, 0.08), rgba(235, 247, 255, 0.18)),
                        url(${loginBackground});
                    background-size: cover;
                    background-position: center;
                    filter: saturate(0.96) contrast(0.98);
                    opacity: 0.96;
                    transform: scale(1.01);
                    animation: wm-login-bg-drift 16s ease-in-out infinite alternate;
                }

                .wm-login-page::after {
                    content: "";
                    position: absolute;
                    inset: 0;
                    z-index: 0;
                    background:
                        linear-gradient(90deg, rgba(255,255,255,.26), rgba(255,255,255,.08), rgba(255,255,255,.26)),
                        radial-gradient(circle at 50% 50%, rgba(255,255,255,.04), rgba(232,244,255,.2) 78%);
                    pointer-events: none;
                }

                .wm-login-card {
                    position: relative;
                    z-index: 3;
                    width: min(560px, calc(100vw - 36px));
                    box-sizing: border-box;
                    padding: 48px 52px 44px;
                    border: 1px solid rgba(210, 227, 244, 0.95);
                    border-radius: 18px;
                    background:
                        linear-gradient(180deg, rgba(255, 255, 255, 0.9), rgba(250, 253, 255, 0.82)),
                        rgba(255,255,255,.8);
                    box-shadow: 0 28px 80px rgba(30, 74, 120, 0.24), 0 8px 24px rgba(30, 74, 120, 0.08);
                    backdrop-filter: blur(14px);
                    -webkit-backdrop-filter: blur(14px);
                    text-align: center;
                    overflow: hidden;
                    animation: wm-card-enter 650ms cubic-bezier(.2,.85,.2,1) both;
                }

                .wm-login-card::before {
                    content: "";
                    position: absolute;
                    inset: 0 0 auto;
                    height: 4px;
                    background: linear-gradient(90deg, #2785ff, #1cb6cc, #8c72ed);
                    opacity: .85;
                }

                .wm-login-card::after {
                    content: "";
                    position: absolute;
                    inset: auto -10% -15% -10%;
                    height: 34%;
                    background: repeating-radial-gradient(ellipse at 50% 120%, rgba(39,133,255,.16) 0 1px, transparent 1px 15px);
                    opacity: .55;
                    pointer-events: none;
                }

                .wm-login-logo {
                    width: 150px;
                    height: 104px;
                    object-fit: contain;
                    margin: 0 auto 12px;
                    display: block;
                    filter: drop-shadow(0 8px 14px rgba(13, 43, 80, 0.12));
                    animation: wm-logo-float 4.8s ease-in-out infinite;
                }

                .wm-login-title {
                    margin: 0;
                    color: #102557;
                    font-size: clamp(30px, 4vw, 42px);
                    font-weight: 760;
                    letter-spacing: -1px;
                }

                .wm-login-subtitle {
                    margin: 8px 0 28px;
                    color: #6680a4;
                    font-size: 17px;
                }

                .wm-login-field {
                    position: relative;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    height: 56px;
                    margin-bottom: 14px;
                    padding: 0 14px;
                    border: 1px solid #cddff2;
                    border-radius: 10px;
                    background: rgba(255, 255, 255, 0.72);
                    box-shadow: inset 0 1px 0 rgba(255,255,255,.85), 0 2px 8px rgba(28, 82, 140, 0.04);
                    transition: border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease;
                }

                .wm-login-field:focus-within {
                    border-color: #2785ff;
                    box-shadow: 0 0 0 3px rgba(39, 133, 255, 0.12), 0 10px 24px rgba(28, 82, 140, 0.08);
                    transform: translateY(-1px);
                }

                .wm-login-field i {
                    width: 34px;
                    height: 34px;
                    display: grid;
                    place-items: center;
                    border-radius: 50%;
                    background: #eef6ff;
                    color: #0f67c4;
                    flex: 0 0 auto;
                }

                .wm-login-field input {
                    width: 100%;
                    min-width: 0;
                    height: 100%;
                    border: 0;
                    outline: 0;
                    background: transparent;
                    color: #102557;
                    font: 600 15px/1.3 "Segoe UI", Arial, sans-serif;
                }

                .wm-login-field input::placeholder { color: #7890af; font-weight: 500; }

                .wm-login-password-button {
                    width: 36px;
                    height: 36px;
                    border: 0;
                    border-radius: 50%;
                    display: grid;
                    place-items: center;
                    background: transparent;
                    color: #426895;
                    cursor: pointer;
                }

                .wm-login-password-button:hover { background: #eef6ff; color: #0f67c4; }

                .wm-login-error {
                    margin: 4px 0 14px;
                    padding: 9px 12px;
                    border-radius: 9px;
                    background: #fff0f2;
                    color: #c6283e;
                    font-size: 13px;
                    font-weight: 650;
                    animation: wm-error-shake 260ms ease;
                }

                .wm-login-turnstile {
                    position: relative;
                    z-index: 1;
                    display: flex;
                    justify-content: center;
                    min-height: 65px;
                    margin: 4px 0 14px;
                }

                .wm-login-turnstile-placeholder {
                    width: 100%;
                    min-height: 58px;
                    display: grid;
                    place-items: center;
                    padding: 12px 14px;
                    box-sizing: border-box;
                    border: 1px dashed #b9cde5;
                    border-radius: 10px;
                    background: rgba(255, 255, 255, 0.58);
                    color: #526b8f;
                    font-size: 13px;
                    font-weight: 650;
                }

                .wm-login-turnstile-error {
                    margin: -4px 0 14px;
                    color: #c6283e;
                    font-size: 13px;
                    font-weight: 650;
                }

                .wm-login-submit {
                    width: 100%;
                    height: 58px;
                    margin-top: 8px;
                    border: 0;
                    border-radius: 10px;
                    background: linear-gradient(180deg, #1680eb, #0f67c4);
                    color: #fff;
                    box-shadow: 0 12px 24px rgba(17, 108, 210, 0.28);
                    font: 750 18px/1 "Segoe UI", Arial, sans-serif;
                    cursor: pointer;
                    transition: transform 160ms ease, box-shadow 160ms ease, filter 160ms ease;
                }

                .wm-login-submit:hover { transform: translateY(-2px); box-shadow: 0 16px 30px rgba(17, 108, 210, 0.34); }
                .wm-login-submit:active { transform: translateY(0); }
                .wm-login-submit:disabled { cursor: progress; filter: saturate(.72); }

                .wm-login-submit i { margin-right: 12px; }

                .wm-login-divider {
                    display: flex;
                    align-items: center;
                    gap: 14px;
                    margin: 24px 0 18px;
                    color: #6d85a7;
                    font-size: 12px;
                }

                .wm-login-divider::before,
                .wm-login-divider::after {
                    content: "";
                    height: 1px;
                    flex: 1;
                    background: #dbe8f6;
                }

                .wm-login-links {
                    position: relative;
                    z-index: 1;
                    display: flex;
                    justify-content: center;
                    gap: 0;
                    flex-wrap: wrap;
                }

                .wm-login-links a {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    padding: 0 18px;
                    color: #0f4fb3;
                    text-decoration: none;
                    font-size: 13px;
                    font-weight: 700;
                    min-height: 28px;
                    border-right: 1px solid #d8e4f2;
                }

                .wm-login-links a:last-child { border-right: 0; }
                .wm-login-links a:hover { color: #0f67c4; text-decoration: underline; }

                .wm-login-success {
                    margin-top: 14px;
                    color: #09865d;
                    font-weight: 700;
                    animation: wm-card-enter 320ms ease both;
                }

                @keyframes wm-card-enter {
                    from { opacity: 0; transform: translateY(18px) scale(.98); }
                    to { opacity: 1; transform: translateY(0) scale(1); }
                }
                @keyframes wm-logo-float {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-5px); }
                }
                @keyframes wm-login-bg-drift {
                    from { transform: scale(1.01) translate3d(-5px, 0, 0); }
                    to { transform: scale(1.035) translate3d(5px, -3px, 0); }
                }

                @keyframes wm-error-shake {
                    0%, 100% { transform: translateX(0); }
                    30% { transform: translateX(-5px); }
                    70% { transform: translateX(5px); }
                }

                @media (max-width: 720px) {
                    .wm-login-card { padding: 34px 22px 32px; }
                    .wm-login-logo { width: 126px; height: 86px; }
                    .wm-login-links a { width: 100%; justify-content: center; border-right: 0; margin-bottom: 6px; }
                    .wm-login-turnstile { transform: scale(.9); transform-origin: center; }
                }
            `}</style>

            <main className="wm-login-page">
                <section className="wm-login-card" aria-label="Sign in">
                    <img src={biglogo} alt="EZYweigh" className="wm-login-logo" />
                    <h1 className="wm-login-title">Welcome back</h1>
                    <p className="wm-login-subtitle">Sign in to manage weighbridge operations</p>

                    <label className="wm-login-field" aria-label="User name">
                        <i className="fa-solid fa-user" aria-hidden="true" />
                        <input
                            required
                            type="text"
                            placeholder="User Name"
                            name="username"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            onKeyDown={submitOnEnter}
                            autoComplete="username"
                        />
                    </label>

                    <label className="wm-login-field" aria-label="Password">
                        <i className="fa-solid fa-lock" aria-hidden="true" />
                        <input
                            required
                            type={showPassword ? "text" : "password"}
                            placeholder="Password"
                            name="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            onKeyDown={submitOnEnter}
                            autoComplete="current-password"
                        />
                        <button
                            type="button"
                            className="wm-login-password-button"
                            onClick={() => setShowPassword((visible) => !visible)}
                            aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                            <i className={showPassword ? "fa-solid fa-eye-slash" : "fa-solid fa-eye"} aria-hidden="true" />
                        </button>
                    </label>

                    {fail && <div className="wm-login-error">Invalid user name or password</div>}
                    <div className="wm-login-turnstile">
                        {turnstileSiteKey ? (
                            <div ref={turnstileRef} />
                        ) : (
                            <div className="wm-login-turnstile-placeholder">Cloudflare Turnstile site key is not configured.</div>
                        )}
                    </div>
                    {turnstileError && <div className="wm-login-turnstile-error">{turnstileError}</div>}

                    <button
                        type="button"
                        className="wm-login-submit"
                        onClick={submitted}
                        disabled={isSubmitting || loginSuccess || (turnstileSiteKey && !turnstileToken)}
                    >
                        <i className={isSubmitting ? "fa-solid fa-spinner fa-spin" : "fa-solid fa-chevron-right"} aria-hidden="true" />
                        {loginSuccess ? "Signed in" : isSubmitting ? "Signing in..." : "Sign In"}
                    </button>

                    {loginSuccess && <div className="wm-login-success">Login successful. Opening your workspace...</div>}

                    <div className="wm-login-divider">or continue with</div>
                    <nav className="wm-login-links" aria-label="Helpful links">
                        <a href="https://wms.screenconnect.com" target="_blank" rel="noreferrer"><i className="fa-solid fa-headset" aria-hidden="true" /> Remote support</a>
                        <a href="https://weigh-more.com.au/contact/" target="_blank" rel="noreferrer"><i className="fa-solid fa-phone" aria-hidden="true" /> Contact us</a>
                        <a href="https://weigh-more.com.au/about-us/" target="_blank" rel="noreferrer"><i className="fa-solid fa-circle-info" aria-hidden="true" /> About</a>
                    </nav>
                </section>
            </main>
        </>
    );
};

export default SignIn;




